import { Form } from "antd";
import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { getForm, getUpdatedGridRows } from "../../../../services/restAPI/RestApi";
import { useDispatch, useSelector } from "react-redux";
import Grid from "./Grid";
import { useFormContext } from "../../../../context/FormContext/FormContext";
import { handleGridTechname, renderDevInfo } from "../../GeneralScripts";
import { setGridFormRefs, setGridRequestFieldId } from "../../../../appRedux/actions/FormsV2";
import { Message } from "../../../BP2Components";
import "./TableField.less";

const TableField = ({ isRequired, element, form, isVisible, disabled, formUuid }) => {
   const dispatch = useDispatch();
   const intl = useIntl();
   const { formInfo } = useSelector(({ formsv2 }) => formsv2);
   const { getFieldDecorator } = form;
   const { formData, setFormData, setIsAnyFieldLoading } = useFormContext();
   const [formElements, setFormElements] = useState([]);
   const [fetchedData, setFetchedData] = useState([]);
   const [rows, setRows] = useState([]);

   useEffect(() => {
      const handleGridData = async () => {
         try {
            // First we need to fetch form structure for grid. This allows us to assign correct columns.
            const gridFormData = await getForm(element.num1_updated, null, null, null, null, true);
            const fetchedFormElements = Object.values(
               gridFormData.data.default.mainForm.forms_data.tab[0].elements,
            );

            // Then we need to fetch data of grid. This is done with the same request, but we need to pass more arguments
            const mainFormWithGridData = await getForm(
               formInfo[formUuid].formId,
               formInfo[formUuid].dataId,
               [element.num1_updated],
            );

            // Api returns array of objects of subforms so we need to somehow access this info. Code below makes it more readable later.
            const fetchedData = mainFormWithGridData.data.default.subForms[0][element.num1_updated];

            const updatedRowsData = await Promise.all(
               fetchedData.map((rowData) =>
                  getUpdatedGridRows(element.num1_updated, rowData, formData._formKey),
               ),
            );

            const fetchedRows = fetchedData.map((rowData, index) => ({
               ...updatedRowsData[index].data.updateRowLabels,
               mainRecordId:
                  rowData.metaData.mainRecordId ??
                  Math.floor(Math.random() * 1000000000 + 1000000000),
               existingRecord: rowData.existingRecord,
               key: index,
            }));

            // Repair data from api - precisions in rowLabelValues
            const repairedValuesRows = fetchedRows.map((row) => {
               const repairedRow = {};
               Object.keys(row).forEach((key) => {
                  if (typeof row[key] === "object") {
                     repairedRow[key] = { ...row[key] };
                     const cellValue = row[key]?.value;
                     if (
                        row[key]?.type === "dec" &&
                        cellValue !== "" &&
                        !Number.isNaN(+cellValue)
                     ) {
                        const element = fetchedFormElements.find((obj) => obj.tech_name === key);
                        const precision = element ? element.precision : "2";
                        const parsedValue = parseFloat(cellValue);
                        const repairedValue = parsedValue.toFixed(precision);
                        repairedRow[key].value = repairedValue.toString();
                     }
                  } else {
                     repairedRow[key] = row[key];
                  }
               });
               return repairedRow;
            });

            setFormData(mainFormWithGridData?.data?.default?.mainForm);
            setFetchedData(fetchedData);
            setFormElements(fetchedFormElements);
            setRows(repairedValuesRows);
         } catch (err) {
            console.error(err);
            Message.error(intl.formatMessage({ id: "forms.handleGridDataError" }));
         } finally {
            setIsAnyFieldLoading(false);
         }
      };

      handleGridData();

      // Reset refs from the table after compoenent is unmounted
      return () => dispatch(setGridFormRefs([]));
   }, []);

   useEffect(() => {
      if (element?.num1_updated) {
         dispatch(setGridRequestFieldId(element?.num1_updated));
      }

      return () => dispatch(setGridRequestFieldId(null));
   }, [element?.num1_updated]);

   useEffect(() => {
      // Refresh grid data
      const newGridValue = form.getFieldValue(element.tech_name);
      if (Array.isArray(newGridValue)) {
         const newRows = [];
         // Prepare new rows with data
         newGridValue.forEach((row, i) => {
            const newRow = {};
            newRow.mainRecordId = Math.floor(Math.random() * 1000000000 + 1000000000);
            newRow.key = i;
            newRow.existingRecord = true;
            Object.keys(row).forEach((key) => {
               const element = formElements.find((el) => el.label === key);
               const elementTechname = element?.tech_name;
               const type = element?.enum;
               const cellValue = row[key];
               newRow[elementTechname] = { type: type, value: cellValue };
            });
            newRows.push(newRow);
         });
         setRows(newRows);
      }
   }, [JSON.stringify(form.getFieldValue(element.tech_name))]);

   return (
      <Form.Item
         key={element.tech_name}
         className={`${element?.type}-field field_${element.tech_name} _type_${element?.type}_type_`}
         style={{
            display: isVisible(element) ? "" : "none",
            gridArea: handleGridTechname(element.tech_name),
         }}
      >
         {getFieldDecorator(element.tech_name, {
            rules: [
               {
                  required: isRequired(element),
                  message: intl.formatMessage({ id: "field.required" }),
               },
            ],
         })(
            <Grid
               form={form}
               fetchedData={fetchedData}
               hideManagementButtons={element?.hideManagementButtons}
               formElements={formElements}
               rows={rows}
               disabled={disabled}
               gridFormId={element.num1_updated}
               formUuid={formUuid}
               mainForm={formData}
            />,
         )}
         {renderDevInfo(element)}
      </Form.Item>
   );
};

export default TableField;
